import { AppBase, StandardMaterial } from "playcanvas";
import { assert } from "@animech/core-utilities";
import {
	createTextureFromSvg,
	createTransparentSpriteMaterial,
} from "@animech/axiom-wall-builder/src/utility/playcanvas";
import { cacheBust } from "../../utility/cache-bust";

export type ControllerIconUrls = InstanceType<
	typeof ControllerAssets
>["_iconUrls"];

export type ControllerIconMaterials = Record<
	keyof ControllerIconUrls,
	StandardMaterial
>;

export type ControllerAssetOptions = {
	iconUrls?: Partial<ControllerIconUrls>;
};

class ControllerAssets {
	private _iconUrls = {
		moveAllIcon: cacheBust("resources/images/controller-icons/move-all.svg"),
		rotateIcon: cacheBust("resources/images/controller-icons/rotate.svg"),
	};

	private _iconMaterials?: ControllerIconMaterials;

	get iconMaterials(): ControllerIconMaterials {
		assert(!!this._iconMaterials, "Assets have not been initialized");
		return this._iconMaterials;
	}

	async initialize(
		app: AppBase,
		options?: ControllerAssetOptions,
	): Promise<void> {
		if (this._iconMaterials) {
			console.warn("Assets have already been initialized");
			return;
		}

		const iconUrls = {
			...this._iconUrls,
			...options?.iconUrls,
		};
		this._iconMaterials = Object.fromEntries(
			await Promise.all(
				Object.entries(iconUrls).map(
					async ([iconName, iconUrl]) =>
						[
							iconName,
							// On WebGPU no HTMLImageElement may reach Texture#setSource; the icons
							// rely on Axiom Wall Builder 8.1+ having createTextureFromSvg draw into a canvas.
							createTransparentSpriteMaterial(
								await createTextureFromSvg(iconUrl, app),
							),
						] as const,
				),
			),
		) as ControllerIconMaterials;
	}
}

export const controllerAssets = new ControllerAssets();
